import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegram } from '@/shared/hooks/useTelegram';
import styles from './ThemeSwitcher.module.css';

type Theme = 'light' | 'dark';

function getSystemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme(): Theme | null {
  const stored = localStorage.getItem('theme');
  return stored === 'light' || stored === 'dark' ? stored : null;
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeSwitcher() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme() ?? getSystemTheme());
  const { haptic } = useTelegram();

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Подписка на системные изменения (только если пользователь не выбрал явно)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!getStoredTheme()) {
        const newTheme = e.matches ? 'dark' : 'light';
        setTheme(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const toggle = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <button
      type="button"
      onClick={() => {
        haptic('selection');
        toggle();
      }}
      className={styles.button}
      aria-label={t(theme === 'dark' ? 'common.switch_to_light_theme' : 'common.switch_to_dark_theme')}
      title={t(theme === 'dark' ? 'common.switch_to_light_theme' : 'common.switch_to_dark_theme')}
    >
      {theme === 'dark' ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M10 3V1M10 19V17M17 10H19M1 10H3M15.657 4.343L17.071 2.929M2.929 17.071L4.343 15.657M15.657 15.657L17.071 17.071M2.929 2.929L4.343 4.343M14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M17 10.5C16.0832 11.3382 14.8916 11.8344 13.6 11.9C11.8578 12.0053 10.1469 11.3681 8.89981 10.1429C7.65272 8.91767 6.98527 7.21597 7.06 5.47C7.09 4.47 7.35 3.53 7.79 2.7C5.65015 3.42742 3.90596 5.0161 2.96122 7.09135C2.01648 9.1666 1.94493 11.5465 2.76261 13.6776C3.58029 15.8088 5.21956 17.513 7.3125 18.4219C9.40544 19.3307 11.7896 19.3682 13.9097 18.5256C16.0299 17.6831 17.7161 16.0296 18.5824 13.9182C19.4488 11.8067 19.425 9.41852 18.5159 7.32405C18.0882 6.32 17.58 5.5 17 4.85"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}
